/** How long the wishlist button stays visible after the slide-in finishes */
const DEFAULT_WISHLIST_REVEAL_MS = 5000;
/** Slide animation duration (reveal + dismiss); synced to CSS via --cart-line-item-slide-duration */
const DEFAULT_SLIDE_DURATION_MS = 400;

const pendingCartRefresh = new WeakMap();

function getCartDrawer() {
  return document.getElementById("cart-drawer");
}

function getTiming(drawer = getCartDrawer()) {
  const revealMs = parseInt(drawer?.dataset.wishlistRevealMs ?? "", 10);
  const slideMs = parseInt(drawer?.dataset.slideDurationMs ?? "", 10);

  return {
    revealMs:
      Number.isFinite(revealMs) && revealMs > 0
        ? revealMs
        : DEFAULT_WISHLIST_REVEAL_MS,
    slideMs:
      Number.isFinite(slideMs) && slideMs > 0
        ? slideMs
        : DEFAULT_SLIDE_DURATION_MS,
  };
}

async function addToWishlistProduct(item) {
  const wishlist = window.WishlistG;

  if (!wishlist?.add) {
    throw new Error("WishlistG is not available");
  }

  const productId = String(item?.productId ?? "");

  if (!productId) {
    throw new Error("Missing product id");
  }

  await wishlist.ready;

  return wishlist.add({
    type: "product",
    refId: productId,
    handle: item?.handle || "",
    title: item?.title || "",
    image: item?.image || "",
    url: item?.url || "",
    price: item?.price || "",
    priceAmount: item?.priceAmount ?? "",
    vendor: item?.vendor || "",
    badge: item?.badge || "",
  });
}

function applyTimingVariables(drawer = getCartDrawer()) {
  if (!drawer) {
    return;
  }

  const { revealMs, slideMs } = getTiming(drawer);
  drawer.style.setProperty("--cart-line-item-slide-duration", `${slideMs}ms`);
  drawer.style.setProperty("--cart-line-item-dismiss-duration", `${slideMs}ms`);
  return { revealMs, slideMs };
}

function extractSectionId(element) {
  const section = element.classList?.contains("shopify-section")
    ? element
    : element.closest(".shopify-section");
  return section?.id?.replace("shopify-section-", "") ?? "";
}

function updateCartCounts(cart) {
  document.querySelectorAll("cart-count").forEach((el) => {
    el.itemCount = cart.item_count;
  });
}

function updateCartDrawerTitles(drawer, itemCount) {
  drawer.querySelectorAll(".cart_bag_title").forEach((title) => {
    if (itemCount === 0) {
      title.remove();
      return;
    }

    const suffix = itemCount === 1 ? " ( 1 ITEM )" : ` ( ${itemCount} ITEMS )`;
    const label = title.querySelector("span");
    if (label) {
      title.replaceChildren(label, document.createTextNode(suffix));
    }
  });
}

function updateFreeShippingBar(cart) {
  const hasItems = cart.item_count > 0;

  document.querySelectorAll("free-shipping-bar").forEach((bar) => {
    bar.hidden = !hasItems;
    if (!hasItems) {
      return;
    }

    const priceForItems = cart.items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscount = cart.cart_level_discount_applications.reduce(
      (sum, allocation) => sum + allocation.total_allocated_amount,
      0,
    );
    bar.totalPrice = priceForItems - cartDiscount;
  });
}

function isWishlistRevealActive(drawer) {
  return Boolean(drawer?.querySelector("line-item[data-wishlist-reveal]"));
}

function shouldDeferCartRefresh(drawer, changingLineKey) {
  if (!drawer?.hasAttribute("data-defer-cart-refresh")) {
    return false;
  }

  const deferredLineKey = drawer.getAttribute("data-defer-line-key");
  if (!deferredLineKey || changingLineKey !== deferredLineKey) {
    return false;
  }

  return Boolean(
    drawer.querySelector(`line-item[data-line-key="${deferredLineKey}"]`),
  );
}

function shouldPartialUpdate(drawer, changingLineKey) {
  return (
    isWishlistRevealActive(drawer) &&
    !shouldDeferCartRefresh(drawer, changingLineKey)
  );
}

function replaceNode(current, next) {
  if (current && next) {
    current.replaceWith(document.importNode(next, true));
  }
}

function parseCartDrawerFromSection(sectionHtml, sectionId) {
  if (!sectionHtml || !sectionId) {
    return null;
  }

  const domElement = new DOMParser().parseFromString(sectionHtml, "text/html");
  return domElement
    .getElementById(`shopify-section-${sectionId}`)
    ?.querySelector("cart-drawer");
}

function updateCartDrawerPrices(drawer, sectionHtml, sectionId, cart) {
  const sourceDrawer = parseCartDrawerFromSection(sectionHtml, sectionId);

  if (sourceDrawer) {
    replaceNode(
      drawer.querySelector(".price_display"),
      sourceDrawer.querySelector(".price_display"),
    );
    replaceNode(
      drawer.querySelector(".buy-buttons__price"),
      sourceDrawer.querySelector(".buy-buttons__price"),
    );
    return;
  }

  if (cart?.total_price == null) {
    return;
  }

  const formatter = new Intl.NumberFormat(Shopify.locale, {
    style: "currency",
    currency: Shopify.currency.active,
  });
  const formattedTotal = formatter.format(cart.total_price / 100);

  drawer
    .querySelector(".price_display .pr_dis-1 p:last-child")
    ?.replaceChildren(formattedTotal);
  drawer.querySelector(".buy-buttons__price")?.replaceChildren(formattedTotal);
}

function updateLineItemFromSection(drawer, sectionHtml, sectionId, lineKey) {
  const sourceDrawer = parseCartDrawerFromSection(sectionHtml, sectionId);
  const newLineItem = sourceDrawer?.querySelector(
    `line-item[data-line-key="${lineKey}"]`,
  );
  const currentLineItem = drawer.querySelector(
    `line-item[data-line-key="${lineKey}"]`,
  );

  if (
    !newLineItem ||
    !currentLineItem ||
    currentLineItem.hasAttribute("data-wishlist-reveal")
  ) {
    return;
  }

  currentLineItem.replaceWith(document.importNode(newLineItem, true));
}

function applyLineItemKey(lineItem, lineKey) {
  if (!lineItem || !lineKey) {
    return;
  }

  lineItem.setAttribute("data-line-key", lineKey);
  lineItem.querySelectorAll("[data-line-key]").forEach((node) => {
    node.setAttribute("data-line-key", lineKey);
  });
  lineItem.querySelectorAll('a[href*="/cart/change"]').forEach((link) => {
    try {
      const url = new URL(link.getAttribute("href"), window.location.origin);
      if (url.searchParams.has("id")) {
        url.searchParams.set("id", lineKey);
        link.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      /* ignore malformed href */
    }
  });
}

function syncRemainingLineItems(drawer, sectionHtml, sectionId, cart) {
  if (!drawer) {
    return;
  }

  const currentItems = [...drawer.querySelectorAll("line-item")];
  const sourceDrawer = parseCartDrawerFromSection(sectionHtml, sectionId);

  if (sourceDrawer) {
    const sourceItems = [...sourceDrawer.querySelectorAll("line-item")];
    const used = new Set();

    currentItems.forEach((current) => {
      if (current.hasAttribute("data-wishlist-reveal")) {
        return;
      }

      const currentKey = current.dataset.lineKey;
      const exact = sourceItems.find(
        (item) => item.dataset.lineKey === currentKey && !used.has(item),
      );

      if (exact) {
        used.add(exact);
        current.replaceWith(document.importNode(exact, true));
        return;
      }

      const match = sourceItems.find(
        (item) =>
          item.dataset.variantId &&
          item.dataset.variantId === current.dataset.variantId &&
          !used.has(item),
      );

      if (match) {
        used.add(match);
        current.replaceWith(document.importNode(match, true));
      }
    });
    return;
  }

  if (!cart?.items) {
    return;
  }

  const claimed = new Set();

  currentItems.forEach((current) => {
    if (current.hasAttribute("data-wishlist-reveal")) {
      return;
    }

    const currentKey = current.dataset.lineKey;
    if (cart.items.some((item) => item.key === currentKey)) {
      claimed.add(currentKey);
    }
  });

  currentItems.forEach((current) => {
    if (current.hasAttribute("data-wishlist-reveal")) {
      return;
    }

    const currentKey = current.dataset.lineKey;
    if (cart.items.some((item) => item.key === currentKey)) {
      return;
    }

    const variantId = String(current.dataset.variantId || "");
    const match = cart.items.find(
      (item) => String(item.variant_id) === variantId && !claimed.has(item.key),
    );

    if (match) {
      claimed.add(match.key);
      applyLineItemKey(current, match.key);
    }
  });
}

async function applyPendingCartRefresh(drawer) {
  const pending = pendingCartRefresh.get(drawer);
  if (!pending) {
    return;
  }

  let html = pending.html;
  if (!html && pending.sectionId) {
    html = await fetch(
      `${Shopify.routes.root}?section_id=${pending.sectionId}`,
    ).then((response) => response.text());
  }

  if (!html) {
    pendingCartRefresh.delete(drawer);
    drawer.removeAttribute("data-defer-cart-refresh");
    drawer.removeAttribute("data-defer-line-key");
    return;
  }

  const domElement = new DOMParser().parseFromString(html, "text/html");
  const sectionRoot = domElement.getElementById(
    `shopify-section-${pending.sectionId}`,
  );
  const newCartDrawer = document
    .createRange()
    .createContextualFragment(
      sectionRoot.querySelector("cart-drawer").innerHTML,
    );

  drawer.replaceChildren(...newCartDrawer.children);
  drawer.classList.toggle("drawer--center-body", pending.itemCount === 0);
  drawer.removeAttribute("data-defer-cart-refresh");
  drawer.removeAttribute("data-defer-line-key");
  pendingCartRefresh.delete(drawer);
  applyTimingVariables(drawer);
  drawer.dispatchEvent(
    new CustomEvent("cart-drawer:refreshed", { bubbles: true }),
  );
}

function syncCartFromEvent(event) {
  const cart = event.detail?.cart;
  if (!cart) {
    return;
  }

  updateCartCounts(cart);
  updateFreeShippingBar(cart);

  const drawer = getCartDrawer();
  if (drawer) {
    updateCartDrawerTitles(drawer, cart.item_count);
  }
}

function waitForTransition(element, propertyName, durationMs) {
  return new Promise((resolve) => {
    let settled = false;

    const done = () => {
      if (settled) {
        return;
      }
      settled = true;
      element.removeEventListener("transitionend", onEnd);
      resolve();
    };

    const onEnd = (event) => {
      if (event.target !== element || event.propertyName !== propertyName) {
        return;
      }
      done();
    };

    element.addEventListener("transitionend", onEnd);
    window.setTimeout(done, durationMs + 50);
  });
}

class LineItem extends HTMLElement {
  connectedCallback() {
    applyTimingVariables(this.closest("cart-drawer"));

    this.#wishlistBtn = this.querySelector("[data-line-item-wishlist-btn]");
    this.#wishlistPanel = this.querySelector(".line-item__panel--wishlist");

    this.addEventListener("line-item:will-change", this.#onWillChange);
    this.addEventListener("line-item:change", this.#onChange);
    this.#wishlistBtn?.addEventListener("click", this.#onWishlistClick);
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.removeEventListener("line-item:will-change", this.#onWillChange);
    this.removeEventListener("line-item:change", this.#onChange);
    this.#wishlistBtn?.removeEventListener("click", this.#onWishlistClick);
  }

  #wishlistBtn;
  #wishlistPanel;
  #hideTimer;
  #dismissed = false;
  #revealStarted = false;
  /** Set when the user explicitly sets quantity to 0 (remove), cleared on any other change */
  #pendingRemoval = false;

  #clearDeferredRefresh() {
    const drawer = this.closest("cart-drawer");
    if (!drawer) {
      return;
    }

    drawer.removeAttribute("data-defer-cart-refresh");
    drawer.removeAttribute("data-defer-line-key");
  }

  #onWillChange = (event) => {
    const targetQuantity = Number(event.detail?.targetQuantity);

    if (targetQuantity !== 0) {
      this.#pendingRemoval = false;
      return;
    }

    this.#pendingRemoval = true;

    const drawer = this.closest("cart-drawer");
    if (!drawer) {
      return;
    }

    drawer.setAttribute("data-defer-cart-refresh", "true");
    drawer.setAttribute("data-defer-line-key", this.dataset.lineKey ?? "");
  };

  #onChange = (event) => {
    if (!this.#pendingRemoval) {
      return;
    }

    this.#pendingRemoval = false;

    const quantity = Number(event.detail?.quantity);
    if (quantity !== 0) {
      this.#clearDeferredRefresh();
      return;
    }

    if (!this.closest("cart-drawer")) {
      this.#clearDeferredRefresh();
      return;
    }

    this.#beginWishlistReveal();
  };

  #onWishlistClick = async (event) => {
    event.preventDefault();

    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;
    const lineKey = this.dataset.lineKey;

    this.#wishlistBtn?.setAttribute("aria-busy", "true");
    this.#wishlistBtn?.toggleAttribute("disabled", true);

    try {
      await addToWishlistProduct({
        productId,
        handle: this.dataset.productHandle || "",
        title: this.dataset.productTitle || "",
        url: this.dataset.productUrl || "",
        image: this.dataset.productImage || "",
        price: this.dataset.productPrice || "",
        priceAmount: this.dataset.productPriceAmount || "",
        vendor: this.dataset.productVendor || "",
        badge: this.dataset.productBadge || "",
      });
    } catch {
      // Item is already removed from cart; keep UX moving even if wishlist sync fails.
    } finally {
      this.#wishlistBtn?.removeAttribute("aria-busy");
      this.#wishlistBtn?.toggleAttribute("disabled", false);
    }

    this.dispatchEvent(
      new CustomEvent("line-item:wishlist-click", {
        bubbles: true,
        detail: {
          productId,
          variantId,
          lineKey,
        },
      }),
    );
    this.dismissWishlist();
  };

  async #beginWishlistReveal() {
    if (this.#revealStarted) {
      return;
    }
    this.#revealStarted = true;

    const drawer = this.closest("cart-drawer");
    const { revealMs, slideMs } = getTiming(drawer);
    applyTimingVariables(drawer);

    this.#wishlistPanel?.removeAttribute("hidden");

    const track = this.querySelector(".line-item__track");
    if (track) {
      track.style.transition = "none";
      track.offsetHeight;
      track.style.transition = "";
    }

    this.classList.add("is-wishlist-reveal");
    this.setAttribute("data-wishlist-reveal", "");

    if (track) {
      await waitForTransition(track, "transform", slideMs);
    }

    this.#clearTimer();
    this.#hideTimer = window.setTimeout(() => {
      this.dismissWishlist();
    }, revealMs);
  }

  async dismissWishlist() {
    if (this.#dismissed) {
      return;
    }
    this.#dismissed = true;
    this.#clearTimer();

    const drawer = this.closest("cart-drawer");
    const { slideMs } = getTiming(drawer);

    const finish = () => {
      this.remove();
      if (drawer) {
        applyPendingCartRefresh(drawer);
      }
    };

    if (!this.classList.contains("is-wishlist-reveal")) {
      finish();
      return;
    }

    this.classList.add("is-wishlist-dismiss");

    const track = this.querySelector(".line-item__track");
    if (!track) {
      finish();
      return;
    }

    await waitForTransition(track, "transform", slideMs);
    finish();
  }

  #clearTimer() {
    if (this.#hideTimer) {
      window.clearTimeout(this.#hideTimer);
      this.#hideTimer = null;
    }
  }
}

if (!window.customElements.get("line-item")) {
  window.customElements.define("line-item", LineItem);
}

applyTimingVariables();

document.addEventListener(
  "line-item:will-change",
  (event) => {
    const lineItem = event.target.closest?.("line-item") ?? event.target;
    const drawer = lineItem?.closest?.("cart-drawer");
    if (!drawer || !lineItem?.dataset?.lineKey) {
      return;
    }

    drawer.dataset.changingLineKey = lineItem.dataset.lineKey;
  },
  true,
);

document.addEventListener(
  "cart:change",
  (event) => {
    const drawer = getCartDrawer();
    if (!drawer || event.detail?.baseEvent !== "line-item:change") {
      return;
    }

    const changingLineKey = drawer.dataset.changingLineKey ?? "";
    const cart = event.detail.cart;
    const sectionId = extractSectionId(drawer);
    const sectionHtml = cart?.sections?.[sectionId];
    const deferRefresh = shouldDeferCartRefresh(drawer, changingLineKey);
    const partialUpdate = shouldPartialUpdate(drawer, changingLineKey);

    if (!deferRefresh && !partialUpdate) {
      return;
    }

    event.stopImmediatePropagation();

    if (deferRefresh || pendingCartRefresh.has(drawer)) {
      pendingCartRefresh.set(drawer, {
        html: sectionHtml || null,
        sectionId,
        itemCount: cart.item_count,
      });
    }

    if (partialUpdate) {
      updateLineItemFromSection(
        drawer,
        sectionHtml,
        sectionId,
        changingLineKey,
      );
    }

    syncRemainingLineItems(drawer, sectionHtml, sectionId, cart);
    syncCartFromEvent(event);
    updateCartDrawerPrices(drawer, sectionHtml, sectionId, cart);
  },
  true,
);

document.addEventListener("cart:change", (event) => {
  const drawer = getCartDrawer();
  const changingLineKey = drawer?.dataset.changingLineKey ?? "";

  if (
    shouldDeferCartRefresh(drawer, changingLineKey) ||
    shouldPartialUpdate(drawer, changingLineKey)
  ) {
    return;
  }

  syncCartFromEvent(event);
});

document.addEventListener("cart-drawer:refreshed", () => {
  applyTimingVariables();
});
